import { RiArrowUpDownLine } from "@remixicon/react";
import React, {
    ReactElement,
    useEffect,
    useState,
} from "react";
import { Direction } from "../../../agent-v3/board-toolbar/SortBy";
import Dropdown, { DropdownProps } from "../../dropdown/Dropdown";
import SortUpDown from "../../sortable/SortBy";
import Styles from "../Table.module.scss";
import useTableData, { Events, SortColumn } from "../base";
import useRouting from "../../../../contexts/Routing";

export type TableControlsSearchProps = Omit<DropdownProps, "children"> & {
    onSort?: (column: SortColumn | undefined) => void;
    sort?: SortColumn;
}

const SORT_KEY_KEY = "sortKey";
const SORT_DIRECTION_KEY = "sortDirection";

const TableControlsSort = React.forwardRef((
    args: TableControlsSearchProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
): ReactElement => {
    const {
        onSort,
        sort,
        ...rest
    } = args;
    const data = useTableData();
    const [sortColumn, setSortColumn] = data.useSortColumn(onSort);

    const [shouldShow, setShouldShow] = useState<boolean>(false);

    const updatedDataGeneration = data.events.useEvent(Events.UPDATED_DATA);

    const { getSearchParams, historyPushSearch } = useRouting();

    useEffect(() => {
        const search = getSearchParams() as Record<string, string>;
        const key = search[SORT_KEY_KEY];
        const direction = search[SORT_DIRECTION_KEY];
        if (key) {
            setSortColumn({
                columnId: key,
                direction: direction === "1" ? Direction.DESCENDING : Direction.ASCENDING,
            });
        }
    }, [updatedDataGeneration]);

    useEffect(() => {
        const search = getSearchParams() as Record<string, undefined | string | string[] | Record<string, string>>;
        if (sortColumn) {
            search[SORT_KEY_KEY] = sortColumn?.columnId;
            search[SORT_DIRECTION_KEY] = sortColumn?.direction?.toString();
        }
        historyPushSearch(search);
    }, [sortColumn]);

    useEffect(() => {
        setSortColumn(sort);
    }, [sort?.columnId, sort?.direction, data]);

    return (
        <Dropdown {...rest} ref={ref} autoClose="outside" show={shouldShow} onToggle={() => setShouldShow(!shouldShow)}>
            <Dropdown.Toggle
                variant="filter"
                prefixIcon={<RiArrowUpDownLine />}
                onClick={() => {
                    setShouldShow(!shouldShow);
                }}
                className="d-flex align-items-center w-100 text-start"
                // postfixIcon={shouldShowExpand ? <RiArrowDownLine /> : undefined}
                isActive={sortColumn != null}
            >
                <div className="flex-grow-1">
                    <span className="text-dim">Sort by:</span>
                    {" "}
                    {sortColumn ? (data.columns.find(col => col.key === sortColumn.columnId)?.title) : "None"}
                </div>
            </Dropdown.Toggle>
            <Dropdown.Menu className={`p3-2 gap-1 ${Styles["panel-filters"]}`}>
                {data.columns.filter(row => row.sortBy).map(column => (
                    <Dropdown.Item
                        key={column.key}
                        className={Styles["control-sort"]}
                        onClick={() => {
                            setSortColumn({
                                columnId: column.key,
                                direction: sortColumn?.columnId === column.key && sortColumn?.direction === Direction.ASCENDING
                                    ? Direction.DESCENDING
                                    : Direction.ASCENDING,
                            });
                        }}
                        postFixItem={(
                            <SortUpDown
                                variant="buttons"
                                onChange={direction => setSortColumn({
                                    columnId: column.key,
                                    direction,
                                })}
                                direction={sortColumn?.columnId === column.key ? sortColumn?.direction : undefined}
                            />
                        )}
                    >
                        {column.title}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
});

TableControlsSort.defaultProps = {

};

export type Type = typeof TableControlsSort;

export default TableControlsSort;
